import { useEffect, useRef, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';

/* Who may look at a service item's Pack pipeline, and whether they may touch it.
   One answer, in one place, because the tab strip and the pipeline itself have
   to agree — and because the rule is easier to check than to remember. */

export type PackAccess = {
    visible: boolean;
    readOnly: boolean;
};

/* What we know so far about a consumer's right to see this service's Pack.
   'denied' covers every way of not being allowed in — the flag is off, the
   service has no Pack profile, the request failed — because the tab does the
   same thing in all of them. */
export type ConsumerVisibility = 'loading' | 'granted' | 'denied';

export type PackAccessInput = {
    /* "serviceowner" | "consumer" — the prop the detail page is already given */
    pov: string;
    /* the team role from react-admin's usePermissions() */
    permissions?: string;
    /* the tenant-wide AI Integration feature flag */
    aiIntegrationEnabled: boolean;
    /* whether this service item has any AI processors to have run */
    hasAiProcessors: boolean;
    consumerVisibility: ConsumerVisibility;
};

const HIDDEN: PackAccess = { visible: false, readOnly: true };

/* The roles the API lets write to Pack — PackPipelineViewSet and the Pack
   profile actions both check for these two. Mirrored here so the UI never
   offers an action the backend is going to refuse. */
const INTERACTIVE_ROLES = ['ADMIN', 'CONFIGURATION'];

/* Every role that can hold a Pack tab open, interactive or not. An unrecognised
   role — including the undefined one usePermissions() reports before the
   promise settles — gets nothing, so the tab cannot flash into being and then
   turn out to have been unauthorised. */
const READABLE_ROLES = [...INTERACTIVE_ROLES, 'GENERAL', 'READ_ONLY'];

/* The same rule where there is no service item to resolve access for — the
   Pack dashboard's own lists, whose actions hit those same endpoints. An
   unknown role, including the undefined one usePermissions() reports before it
   has settled, cannot write. */
export const canWritePack = (permissions?: string): boolean =>
    !!permissions && INTERACTIVE_ROLES.includes(permissions);

export const resolvePackAccess = ({
    pov,
    permissions,
    aiIntegrationEnabled,
    hasAiProcessors,
    consumerVisibility,
}: PackAccessInput): PackAccess => {
    /* No Pack in this deployment, or none on this item: nothing to show anyone. */
    if (!aiIntegrationEnabled || !hasAiProcessors) return HIDDEN;

    if (pov === 'consumer') {
        /* A consumer sees the Pack only where the owner has said so, and then
           only to read. Loading and failure both mean not yet, not maybe. */
        return consumerVisibility === 'granted'
            ? { visible: true, readOnly: true }
            : HIDDEN;
    }

    if (pov === 'serviceowner') {
        if (!permissions || !READABLE_ROLES.includes(permissions)) return HIDDEN;
        return { visible: true, readOnly: !canWritePack(permissions) };
    }

    return HIDDEN;
};

type UsePackAccessArgs = Omit<PackAccessInput, 'consumerVisibility'> & {
    serviceId?: number;
};

/* The same rules, with the one fact that has to be fetched fetched for you.
   Only the consumer point of view asks the API anything; an owner's answer is
   role and feature flags, which are already in hand. */
export const usePackAccess = ({
    pov,
    permissions,
    aiIntegrationEnabled,
    hasAiProcessors,
    serviceId,
}: UsePackAccessArgs): PackAccess => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    /* The answer is kept with the service it was fetched for. A visibility
       decision only means anything about the service it was asked about, and
       this component is reused as you move between service items. */
    const [answer, setAnswer] = useState<{
        serviceId?: number;
        visibility: ConsumerVisibility;
    }>({ serviceId: undefined, visibility: 'loading' });

    /* Held in a ref rather than named as a dependency: the provider is a
       transport, not a reason to ask again, and one that is rebuilt per render
       would otherwise put this effect in a loop. */
    const dataProviderRef = useRef(dataProvider);
    dataProviderRef.current = dataProvider;

    const asksTheApi = pov === 'consumer' && aiIntegrationEnabled && hasAiProcessors;

    /* Derived, not stored: the moment the service changes we are back to not
       knowing, in the same render. Waiting for the effect would let one paint
       through carrying the previous service's answer, and one paint is enough
       to mount the Pack tab on an item nobody has been cleared to see. */
    const consumerVisibility: ConsumerVisibility =
        answer.serviceId === serviceId ? answer.visibility : 'loading';

    useEffect(() => {
        if (!asksTheApi) return;
        /* Without a service there is nothing to ask about, and no answer is a
           no — not a wait that never ends. */
        if (!serviceId) {
            setAnswer({ serviceId: undefined, visibility: 'denied' });
            return;
        }

        /* Stamped with the service it was asked about, so a slow reply landing
           after you have moved on cannot answer for where you are now. */
        const askedAbout = serviceId;
        let cancelled = false;

        dataProviderRef.current
            .getAction('consumer_pack_profile', {
                id: undefined,
                verb: `service/${askedAbout}/visibility`,
                filter: {},
            })
            .then(({ data }: any) => {
                if (cancelled) return;
                setAnswer({
                    serviceId: askedAbout,
                    visibility: data?.show_pack_to_consumer ? 'granted' : 'denied',
                });
            })
            .catch(() => {
                /* A 404 means no profile, anything else means we could not find
                   out. Neither is permission, so both close the tab. */
                if (!cancelled)
                    setAnswer({ serviceId: askedAbout, visibility: 'denied' });
            });

        return () => {
            cancelled = true;
        };
    }, [asksTheApi, serviceId]);

    return resolvePackAccess({
        pov,
        permissions,
        aiIntegrationEnabled,
        hasAiProcessors,
        consumerVisibility,
    });
};
